
import {
  Component,
  Mixins,
  Prop,
  Provide,
  Watch
} from "vue-property-decorator";
import { IFilter, ISorter } from "@/models/tables";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import { DataModules } from "@/store/modules/data";
import { TemplateTypes } from "@/data/enums/templates";
import { mapState } from "vuex";
import { IState } from "@/store";
import { INotificationHook } from "@/models/notifications";
import {
  ITemplate,
  ITemplateChannel,
  ITemplateCategory,
  ITemplateRecipientType
} from "@/models/templates";
import { NotificationChannelCodes } from "@/data/enums/notifications";
import {
  ITemplatesProviderData,
  ITemplatesProviderMethods
} from "@/models/providers/templates";
import { CreateElement } from "vue";
import {
  DefaultTemplateFilters,
  DefaultClientAreaTemplateFilters,
  DisposeFilters
} from "@/data/filters/templates/index";
import { DefaultTemplateSorters } from "@/data/sorters/templates";
import { ConfirmModalModes } from "@/data/enums";

@Component({
  computed: {
    ...mapState({
      canGet(state, getters) {
        return getters[`data/${DataModules.TEMPLATES}/canGet`](
          this.templateType
        );
      },
      canList(state, getters) {
        return getters[`data/${DataModules.TEMPLATES}/canList`]();
      },
      canEdit(state, getters) {
        return getters[`data/${DataModules.TEMPLATES}/canEdit`](
          this.templateType
        );
      },
      canDelete(state, getters) {
        return getters[`data/${DataModules.TEMPLATES}/canDelete`](
          this.templateType
        );
      },
      withParam(state: IState, getters) {
        return getters[`data/${DataModules.TEMPLATES}/withParam`]({
          type: this.templateType
        });
      },
      channels(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.NOTIFICATIONS_CHANNELS
        });
      },
      recipientTypes(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.NOTIFICATIONS_RECIPIENT_TYPES
        });
      },
      categories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.NOTIFICATIONS_CATEGORIES
        });
      }
    })
  }
})
export default class TemplatesProvider extends Mixins(
  FilteringSortingTableStore
) {
  // Props
  @Prop({ default: "TP" /** Template provider */ })
  public declare id: string;

  @Prop({ required: true })
  public templateType!: TemplateTypes;

  @Prop({ default: null })
  public categoryCode!: ITemplateCategory["code"];

  @Prop({ default: null })
  public categoryId!: ITemplateCategory["id"];

  @Prop({ default: null })
  public channelCode!: NotificationChannelCodes;

  @Prop({ default: null })
  public hookId!: INotificationHook["id"];

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public declare manualFilters: Record<string, string | number | boolean>;

  // Data
  public canList!: boolean;
  public canGet!: boolean;
  public canEdit!: boolean;
  public canDelete!: boolean;
  public withParam!: string;
  public dataModule: DataModules = DataModules.TEMPLATES;
  public channels!: ITemplateChannel[];
  public recipientTypes!: ITemplateRecipientType[];
  public categories!: ITemplateCategory[];
  public debounceSearch = this.$_.debounce(this.doQuickSearch, 250);
  public searchQuery = "";

  public filters: IFilter[] =
    this.templateType === TemplateTypes.CLIENT_TEMPLATE
      ? DefaultClientAreaTemplateFilters()
      : DefaultTemplateFilters();
  public sorters: ISorter[] = DefaultTemplateSorters();

  // Life-cycle methods
  public async created() {
    if (!this.contentIsAvailable) {
      return;
    }
    this.getData();
  }

  public beforeDestroy() {
    DisposeFilters();
  }

  // Provide

  @Provide("templatesProviderMethods")
  public get providerMethods(): ITemplatesProviderMethods {
    return {
      doQuickSearch: this.doQuickSearch,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      debounceSearch: this.debounceSearch,
      reloadData: this.reloadData,
      deleteTemplate: this.deleteTemplate,
      openAddEditModal: this.openAddEditModal
    };
  }

  @Provide("templatesProvider")
  public providerData(): ITemplatesProviderData {
    return {
      availableFilters: this.filters,
      availableSorters: this.sorters,
      activeFilters: this.activeFilters,
      constQueryFilters: this.constQueryFilters,
      categoryId: this.categoryId,
      canGet: this.canGet,
      canEdit: this.canEdit,
      canDelete: this.canDelete,
      activeSort: this.table.sort,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      hasTemplates: this.hasTemplates,
      isLoading: this.table.loading,
      limit: this.table.limit,
      page: this.table.page,
      templates: this.table.data as ITemplate[],
      searchQuery: this.searchQuery,
      table: this.table,
      total: this.table.total
    };
  }

  // Computed
  public get hasTemplates(): boolean {
    return !!this.table.data.length;
  }

  public get channelFilter() {
    return this.channelCode
      ? { ["filter[channel.code]"]: this.channelCode }
      : {};
  }

  public get hookFilter() {
    return this.templateType ? { "filter[mappings.hook_id]": this.hookId } : {};
  }

  public get templateTypeFilter() {
    return this.templateType ? { "filter[type]": this.templateType } : {};
  }

  public get categoryCodeFilter() {
    return this.categoryCode
      ? { "filter[category.code]": this.categoryCode }
      : {};
  }

  public get categoryIdFilter() {
    return this.categoryId ? { "filter[category.id]": this.categoryId } : {};
  }

  public get queryFilter() {
    return this.searchQuery ? { query: this.searchQuery } : {};
  }

  // Methods

  public get contentIsAvailable() {
    return this.canList || this.$userCan("get_notification_channel");
  }

  public async reloadData() {
    this.cancelRequests();
    return this.getData({ ignoreStored: true });
  }

  public doQuickSearch(value = "") {
    if (value === this.searchQuery) return Promise.resolve();
    this.searchQuery = value;
    return this.reloadData();
  }

  // overrides mixin method
  public getStoreParams(params: any) {
    return this.$_.merge(
      {
        params: {
          with: this.withParam,
          ...params.filter,
          ...this.queryFilter,
          ...this.templateTypeFilter,
          ...this.categoryCodeFilter,
          ...this.categoryIdFilter,
          ...this.hookFilter,
          ...this.channelFilter,
          ...this.manualFilters
        }
      },
      params // Important – must merge last
    );
  }

  public async openAddEditModal(
    payload: {
      templateId?: ITemplate["id"];
      props?: any;
      events?: any;
    } = { templateId: undefined, props: {}, events: {} }
  ) {
    const modal = await this.$store.dispatch(
      `data/${DataModules.TEMPLATES}/openAddEditModal`,
      {
        props: {
          templateId: payload.templateId,
          templateType: this.templateType,
          showCode: false,
          ...payload.props
        },
        events: {
          ...payload.events,
          success: async (template: ITemplate) => {
            await this.reloadData();
            if (this.$_.has(payload.events, "success"))
              payload.events.success(template);
          },
          changed: ({ templateId }) => {
            this.openAddEditModal(
              this.$_.merge(payload, { props: { templateId } })
            );
            modal.close();
            this.reloadData();
          }
        }
      }
    );
  }

  public async deleteTemplate(templateId: ITemplate["id"]) {
    if (!this.canDelete) return;
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete_template"),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$t("_.template") as string).toLowerCase()
          }),
          mode: ConfirmModalModes.BUTTON
        },
        events: {
          confirmed: async () => {
            await this.onDeleteConfirmed(templateId);
            confirm.close();
          }
        }
      }
    });
  }

  public async onDeleteConfirmed(templateId: ITemplate["id"]): Promise<any> {
    if (!this.canDelete) return;
    this.table.loading = true;
    try {
      await this.$store.dispatch(`data/${this.dataModule}/remove`, {
        id: templateId
      });
      this.$emit("actions-updated");
      this.reloadData();
      this.$buefy.toast.open({
        message: this.$t("_.object_successfully_removed", {
          object: this.$t("_.template") as string
        }) as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
    this.table.loading = false;
  }

  // Watcher

  @Watch("channelCode")
  public channelChanged() {
    this.getData();
  }

  // Render

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.getData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $templatesData: this.providerData(),
        $templatesMethods: this.providerMethods
      })
    );
  }
}
